import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Crop, MagnifyingGlassPlus, X, Check } from "@phosphor-icons/react";

const PRESETS = [
    { key: "square", label: "Square", ratio: "1:1", aspect: 1, hint: "Instagram" },
    { key: "portrait", label: "Portrait", ratio: "4:5", aspect: 4 / 5, hint: "Instagram" },
    { key: "landscape", label: "Landscape", ratio: "1.91:1", aspect: 1.91, hint: "Facebook · LinkedIn" },
    { key: "original", label: "Original", ratio: "free", aspect: null, hint: "Full frame" },
];

async function getCroppedBlob(src, area, mime) {
    const image = await new Promise((res, rej) => {
        const img = new Image();
        img.onload = () => res(img);
        img.onerror = rej;
        img.src = src;
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(area.width);
    canvas.height = Math.round(area.height);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
    const outType = mime === "image/png" ? "image/png" : "image/jpeg";
    const blob = await new Promise((res) => canvas.toBlob((b) => res(b), outType, 0.92));
    if (!blob) throw new Error("Could not process the image — try a different file.");
    return blob;
}

export default function ImageCropModal({ src, mime, filename, onCancel, onApply }) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [aspectKey, setAspectKey] = useState("square");
    const [area, setArea] = useState(null);
    const [busy, setBusy] = useState(false);
    const preset = PRESETS.find((p) => p.key === aspectKey) || PRESETS[0];

    const onComplete = useCallback((_, px) => setArea(px), []);

    const apply = async () => {
        if (!area) return;
        setBusy(true);
        try {
            const blob = await getCroppedBlob(src, area, mime);
            const ext = (mime === "image/png") ? "png" : "jpg";
            const name = (filename || "image").replace(/\.[^.]+$/, "") + `-cropped.${ext}`;
            onApply(new File([blob], name, { type: blob.type }));
        } catch (e) {
            toast.error(e?.message || "Could not process the image.");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" data-testid="image-crop-modal">
            <div className="flex w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-stone-100 px-5 py-3">
                    <h3 className="inline-flex items-center gap-2 font-heading text-lg font-bold text-ink"><Crop size={18} weight="fill" className="text-brand" /> Crop image</h3>
                    <button data-testid="crop-close" onClick={onCancel} className="rounded-full p-1.5 text-stone2 hover:bg-stone-100"><X size={18} /></button>
                </div>

                <div className="relative h-72 w-full bg-stone-900">
                    <Cropper image={src} crop={crop} zoom={zoom} aspect={preset.aspect || undefined}
                        restrictPosition={false} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
                </div>

                <div className="space-y-4 p-5">
                    <div>
                        <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-stone2">Aspect ratio</div>
                        <div className="grid grid-cols-4 gap-2">
                            {PRESETS.map((p) => (
                                <button key={p.key} data-testid={`crop-preset-${p.key}`} onClick={() => setAspectKey(p.key)}
                                    className={`rounded-xl border px-2 py-2 text-center transition-all ${aspectKey === p.key ? "border-brand bg-brand/5" : "border-stone-200 hover:border-brand/40"}`}>
                                    <div className="text-sm font-bold text-ink">{p.label}</div>
                                    <div className="text-[10px] text-stone-400">{p.ratio}</div>
                                    <div className="mt-0.5 text-[9px] text-stone2">{p.hint}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <MagnifyingGlassPlus size={16} className="text-stone2" />
                        <input data-testid="crop-zoom" type="range" min={1} max={3} step={0.01} value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))} className="w-full accent-[var(--brand,#f2600c)]" />
                    </div>
                </div>

                <div className="flex justify-end gap-2 border-t border-stone-100 px-5 py-3">
                    <button data-testid="crop-cancel" onClick={onCancel} className="rounded-lg border border-stone-200 px-4 py-2 text-sm font-semibold text-stone2 hover:bg-stone-50">Cancel</button>
                    <button data-testid="crop-apply" disabled={busy || !area} onClick={apply}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50">
                        <Check size={15} weight="bold" /> {busy ? "Cropping…" : "Apply & Upload"}
                    </button>
                </div>
            </div>
        </div>
    );
}
